import { debounce } from 'lodash';
import vhCheck from 'vh-check';
import { reactive } from 'vue';

// Breakpoints are kept in sync with tailwind.config.js theme.screens.
// We inline them here instead of importing tailwind.config.js (CJS) to avoid
// Rollup ESM-interop failures in Vite's PWA injectManifest build pass.
const screens = {
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    xxl: '1880px',
};

const stripPx = ((string) => parseInt(string.replace('px', ''), 10));

const sm = (val) => val <= stripPx(screens.md);
const md = (val) => val >= stripPx(screens.md) && val <= stripPx(screens.lg);
const lg = (val) => val >= stripPx(screens.lg) && val <= stripPx(screens.xl);
const xl = (val) => val >= stripPx(screens.xl) && val <= stripPx(screens.xxl);
const xxl = (val) => val >= stripPx(screens.xl);

const getBreakpoint = (w) => {
    if (sm(w)) return 'sm';
    if (md(w)) return 'md';
    if (lg(w)) return 'lg';
    if (xl(w)) return 'xl';
    if (xxl(w)) return 'xxl';
    return 'all';
};

export const breakpoints = reactive({
    w: window.innerWidth,
    h: window.innerHeight,
    is: getBreakpoint(window.innerWidth),
});

export default {
    data() {
        return {
            indentButtons: window.innerHeight <= 1000,
            // eslint-disable-next-line no-restricted-globals
            screenOrientation: (screen.orientation || {}).type || screen.mozOrientation || screen.msOrientation || window.orientation,
        };
    },
    created() {
        // Hold references so beforeUnmount can detach them. Without cleanup the
        // debounced resize handler still fires after the component unmounts
        // (Storybook story switches, route changes), mutating `this.*` on a
        // destroyed instance and leaving a dangling reactive effect that
        // crashes the next patch with "instance is null".
        this.boundResize = debounce(() => {
            vhCheck('browser-address-bar');
            this.indentButtons = window.innerHeight <= 1000;
            breakpoints.w = window.innerWidth;
            breakpoints.h = window.innerHeight;
            breakpoints.is = getBreakpoint(window.innerWidth);
        }, 200);
        this.boundOrientationChange = () => {
            // eslint-disable-next-line no-restricted-globals
            this.screenOrientation = (screen.orientation || {}).type || screen.mozOrientation || screen.msOrientation || window.orientation;
        };
        window.addEventListener('resize', this.boundResize, false);
        window.addEventListener('orientationchange', this.boundOrientationChange);
    },
    beforeUnmount() {
        if (this.boundResize) {
            this.boundResize.cancel?.();
            window.removeEventListener('resize', this.boundResize, false);
        }
        if (this.boundOrientationChange) {
            window.removeEventListener('orientationchange', this.boundOrientationChange);
        }
    },
    computed: {
        isMdAndDownBreakpoint() {
            return this.isSmBreakpoint || this.isMdBreakpoint;
        },
        isLgAndUpBreakpoint() {
            return this.isLgBreakpoint || this.isXlBreakpoint || this.isXxlBreakpoint;
        },
        isXlAndUpBreakpoint() {
            return this.isXlBreakpoint || this.isXxlBreakpoint;
        },
        isSmBreakpoint() {
            return breakpoints.is === 'sm';
        },
        isMdBreakpoint() {
            return breakpoints.is === 'md';
        },
        isLgBreakpoint() {
            return breakpoints.is === 'lg';
        },
        isXlBreakpoint() {
            return breakpoints.is === 'xl';
        },
        isXxlBreakpoint() {
            return breakpoints.is === 'xxl';
        },
        isPortraitMode() {
            return this.screenOrientation === 'portrait-secondary' || this.screenOrientation === 'portrait-primary' || this.screenOrientation === 0 || this.screenOrientation === 180;
        },
    },
};
